import { createStyles } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { ListBox } from '@repo/foundations';
import { useCallback, useState } from 'react';
import type { FILTER_OPTIONS_DIVIDER } from '../constants';
import type { FilterItem, FilterType, FilterValue } from '../types';
import { FilterDropdownList } from './FilterDropdownList';
import { useFilterDropdownDate } from './useFilterDropdownDate';

const useStyles = createStyles((theme) => ({
	calendar: {
		margin: `${theme.spacing['2xs']} ${theme.spacing['2xs']} 0 ${theme.spacing['2xs']}`,
	},
}));

export interface FilterDropdownDateProps {
	value: FilterValue;
	onChange: (value: FilterValue, close: boolean) => void;
	getItems?:
		| (FilterItem | typeof FILTER_OPTIONS_DIVIDER)[]
		| ((
				page?: number,
				searchTerm?: string
		  ) =>
				| (FilterItem | typeof FILTER_OPTIONS_DIVIDER)[]
				| Promise<(FilterItem | typeof FILTER_OPTIONS_DIVIDER)[]>);
	filterType?: FilterType;
}

export function FilterDropdownDate({
	value,
	onChange,
	getItems,
	filterType,
}: FilterDropdownDateProps) {
	const { classes } = useStyles();
	// Always set to true at the beginning because we don't want to show the custom date picker by default
	const [initialOpen, setInitialOpen] = useState(true);

	const { dateValue, handleOnDateChange } = useFilterDropdownDate({
		value,
		onChange,
	});

	const handleOnChange = useCallback(
		(filterValue: FilterValue, close: boolean) => {
			// If the user selects a custom date, the date picker should open
			if (filterValue.value === 'CUSTOM') {
				setInitialOpen(false);
			} else {
				onChange(filterValue, close);
			}
		},
		[onChange]
	);

	if (!dateValue && getItems && initialOpen) {
		return (
			<FilterDropdownList
				value={value}
				filterType={filterType}
				getItems={getItems}
				onChange={handleOnChange}
				searchPlaceholder="Search date"
				hasIsNotSetOption={false}
				hasIsSetOption={false}
			/>
		);
	}

	return (
		<ListBox.Dropdown>
			<DatePicker
				classNames={{ calendar: classes.calendar }}
				type="range"
				allowSingleDateInRange
				defaultDate={dateValue?.[0] ?? new Date()}
				value={dateValue}
				onChange={handleOnDateChange}
			/>
		</ListBox.Dropdown>
	);
}
